import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Divider } from '@mui/material';
import { RouteOverlay } from 'src/components/layouts/RouteOverlay/RouteOverlay';
import { FactsTable } from 'src/components/layouts/FactsTable/FactsTable';
import { OverlaySectionHeadline } from 'src/components/layouts/OverlaySectionHeadline/OverlaySectionHeadline';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { localeDate } from 'src/localization/locale';
import { InvoiceLine, useInvoiceQuery } from 'src/services/invoices';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { downloadWithFeedback } from './downloadWithFeedback';
import { formatMoney, formatPercent, formatQuantity, formatReference, formatUom } from './invoiceFormat';

const LineRow: React.FC<{ line: InvoiceLine; currency: string | null | undefined }> = ({ line, currency }) => {
  const { t } = useTranslation();
  if (line.display_type === 'line_section') {
    return (
      <tr>
        <td colSpan={5} className="pt-16 pb-4 typo-text-12--bold text-faded uppercase">
          {line.name}
        </td>
      </tr>
    );
  }
  if (line.display_type === 'line_note') {
    return (
      <tr>
        <td colSpan={5} className="py-4 typo-text-12 text-faded italic whitespace-pre-wrap">
          {line.name}
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-subtle-top">
      <td className="py-8 pr-12 typo-text-14 text-full whitespace-pre-wrap">
        {line.name}
        {line.product_name && line.product_name !== line.name && (
          <span className="block typo-text-12 text-faded">{line.product_name}</span>
        )}
      </td>
      <td className="py-8 px-12 typo-text-14 text-full text-right whitespace-nowrap">
        {formatQuantity(line.quantity)}
        {line.uom_name ? ` ${formatUom(line.uom_name, t)}` : ''}
      </td>
      <td className="py-8 px-12 typo-text-14 text-full text-right whitespace-nowrap">
        {formatMoney(line.price_unit, currency)}
      </td>
      <td className="py-8 px-12 typo-text-14 text-faded text-right whitespace-nowrap">
        {line.discount ? formatPercent(line.discount) : ''}
      </td>
      <td className="py-8 pl-12 typo-text-14--bold text-full text-right whitespace-nowrap">
        {formatMoney(line.price_subtotal, currency)}
      </td>
    </tr>
  );
};

const InvoiceOverlay: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const invoiceId = Number(id);
  const { data: invoice, isLoading } = useInvoiceQuery(invoiceId);
  const currency = invoice?.currency_code;
  const money = (value: number) => formatMoney(value, currency);
  const facts: [string, React.ReactNode][] = invoice
    ? [
        [t('invoices.columns.status'), <InvoiceStatusBadge status={invoice.status} />],
        [t('invoices.columns.due_date'), invoice.invoice_date_due ? localeDate(invoice.invoice_date_due) : '–'],
        [t('invoices.detail.origin'), invoice.invoice_origin ?? formatReference(invoice.ref, t) ?? '–'],
      ]
    : [];
  if (invoice?.reversal_id) {
    facts.push([
      t('invoices.detail.reversed_by'),
      <Link className="underline" to={`/invoices/${invoice.reversal_id}`}>
        {t('invoices.detail.open_document')}
      </Link>,
    ]);
  }
  if (invoice?.reversed_entry_id) {
    facts.push([
      t('invoices.detail.reverses'),
      <Link className="underline" to={`/invoices/${invoice.reversed_entry_id}`}>
        {t('invoices.detail.open_document')}
      </Link>,
    ]);
  }

  return (
    <RouteOverlay title={invoice?.name}>
      {isLoading && !invoice && <LoadingSkeleton className="h-full" />}
      <ContainerBody className="h-full flex flex-col gap-24">
        <div className="flex items-start justify-between gap-16">
          <div className="flex flex-col gap-8">
            <span className="typo-text-12--bold text-faded uppercase">
              {invoice?.is_credit_note ? t('invoices.types.credit_note') : t('invoices.types.invoice')}
            </span>
            <h2 className="text-full typo-title-36">{invoice?.name ?? ''}</h2>
            {invoice?.invoice_date && (
              <span className="text-faded typo-text-14">
                {t('invoices.detail.issued_on', { date: localeDate(invoice.invoice_date) })}
              </span>
            )}
          </div>
          {invoice && (
            <Button
              tone={ButtonToneProps.PRIMARY_BLUE}
              icon="download"
              title={t('invoices.actions.download_pdf')}
              onClick={() => downloadWithFeedback(invoice, t)}
            />
          )}
        </div>
        {invoice && (
          <>
            <FactsTable entries={facts} />
            <Divider />
            <div>
              <OverlaySectionHeadline>{t('invoices.detail.positions')}</OverlaySectionHeadline>
              {invoice.lines.length === 0 ? (
                <span className="typo-text-14 text-faded">
                  {invoice.lines_available ? t('invoices.detail.no_lines') : t('invoices.detail.lines_unavailable')}
                </span>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="pb-8 pr-12 typo-text-12 text-faded text-left font-normal">
                          {t('invoices.detail.description')}
                        </th>
                        <th className="pb-8 px-12 typo-text-12 text-faded text-right font-normal">
                          {t('invoices.detail.quantity')}
                        </th>
                        <th className="pb-8 px-12 typo-text-12 text-faded text-right font-normal">
                          {t('invoices.detail.unit_price')}
                        </th>
                        <th className="pb-8 px-12 typo-text-12 text-faded text-right font-normal">
                          {t('invoices.detail.discount')}
                        </th>
                        <th className="pb-8 pl-12 typo-text-12 text-faded text-right font-normal">
                          {t('invoices.detail.line_total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lines.map(line => (
                        <LineRow key={line.id} line={line} currency={currency} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <Divider />
            <div className="flex flex-col items-end gap-4">
              <div className="flex gap-24 typo-text-14 text-faded">
                <span>{t('invoices.detail.subtotal')}</span>
                <span className="text-full min-w-[120px] text-right">{money(invoice.amount_untaxed)}</span>
              </div>
              <div className="flex gap-24 typo-text-16--bold text-full">
                <span>{t('invoices.detail.total')}</span>
                <span className="min-w-[120px] text-right">{money(invoice.amount_total)}</span>
              </div>
              <div className="flex gap-24 typo-text-14 text-faded">
                <span>{t('invoices.detail.paid_amount')}</span>
                <span className="min-w-[120px] text-right">
                  {money(invoice.amount_total - invoice.amount_residual)}
                </span>
              </div>
              <div
                className={`flex gap-24 typo-text-14--bold ${invoice.status === 'overdue' ? 'status-error-full' : 'text-full'}`}>
                <span>{t('invoices.detail.residual')}</span>
                <span className="min-w-[120px] text-right">{money(invoice.amount_residual)}</span>
              </div>
            </div>
          </>
        )}
      </ContainerBody>
    </RouteOverlay>
  );
};

export default InvoiceOverlay;
